import { months } from '../../../utils/chartConstants';
import {
  LeftCircleOutlined,
  RightCircleOutlined,
  LoadingOutlined,
} from '@ant-design/icons';
import { useEffect, useState } from 'react';
import Chart from 'chart.js';
import { Row, Col, CardBody, Card } from 'reactstrap';
import { Bar } from 'react-chartjs-2';
import { parseOptions } from 'variables/charts';
import { chartOptions } from 'variables/charts';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { Spin, Empty } from 'antd';
import { filter, round, sumBy, toNumber } from 'lodash-es';
import CorporateChargingSessionsGrid from '../CorporateChargingSessionsGrid';
import { useMonthwiseAnalyticsOfCorporateCharger } from 'utils/queries/corporateAnalytics';

parseOptions(Chart, chartOptions());
Chart.plugins.register(ChartDataLabels);

const ChargerAnalytics = ({ chargerId, setClickedMonthIndex }) => {
  const minYear = 2023;
  const [maxYear, setMaxYear] = useState(0);
  const [selectedYear, setSelectedYear] = useState(null);
  const [energyChartData, setEnergyChartData] = useState(null);
  const [amountChartData, setAmountChartData] = useState(null);
  const [energySoldTotal, setEnergySoldTotal] = useState(0);
  const [netRevenueTotal, setNetRevenueTotal] = useState(0);
  const [filterMonth, setFilterMonth] = useState('');
  const {
    data: chargersData = [],
    isLoading,
    isFetching,
    refetch,
  } = useMonthwiseAnalyticsOfCorporateCharger(chargerId, selectedYear);
  const getNetRevenueForMonth = (month, year) => {
    const obj = filter(chargersData.list, {
      MONTH: month,
      YEAR: year,
    });
    return obj?.length > 0 ? round(obj[0].AMOUNT_CONSUMED / 100, 2) : '0';
  };

  const getEnergySoldForMonth = (month, year) => {
    const obj = filter(chargersData.list, {
      MONTH: month,
      YEAR: year,
    });
    return obj?.length > 0 ? `${round(obj[0].KWH_CONSUMED, 2)} KWh` : '0';
  };

  const refetchChargersData = () => {
    setTimeout(() => refetch(), 300);
  };

  const yearChangeHandler = (action) => {
    let newYear = selectedYear;
    if (action === 'next' && selectedYear < maxYear) newYear += 1;
    else if (action === 'prev' && selectedYear > minYear) newYear -= 1;
    if (newYear !== selectedYear) {
      setSelectedYear(newYear);
      // notifyYearChange(newYear);
      refetchChargersData();
    }
  };

  const getOptions = (type) => {
    const optionsData = {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          grid: {
            display: true,
          },
        },
        yAxes: [
          {
            ticks: {
              display: false,
            },
          },
        ],
      },
      layout: {
        padding: {
          top: 15,
          left: 0,
          right: 0,
          bottom: 0,
        },
      },
      plugins: {
        title: {
          display: true,
          text: type === 'energy' ? 'Energy Sold' : 'Charging Sessions',
        },
        legend: {
          display: true,
        },
        tooltip: {
          displayColors: false,
          bodyFont: { weight: 'bold' },
          callbacks: {
            title: (tooltipItem) => `${tooltipItem[0].label} ${selectedYear}`,
            label: (tooltipItem) => {
              const energy = getEnergySoldForMonth(
                tooltipItem.dataIndex + 1,
                selectedYear,
              );
              return energy
                ? `Energy Sold: ${getEnergySoldForMonth(
                    tooltipItem.dataIndex + 1,
                    selectedYear,
                  )}`
                : '';
            },
            footer: (tooltipItem) => {
              const netRevenue = getNetRevenueForMonth(
                tooltipItem[0].parsed.x + 1,
                selectedYear,
              );
              return netRevenue
                ? `Charging Sessions  : ${getNetRevenueForMonth(
                    tooltipItem[0].parsed.x + 1,
                    selectedYear,
                  )}`
                : '';
            },
          },
        },
      },
      onClick: (evt, ele) => {
        if (!isNaN(ele[0]?._index)) setFilterMonth(ele[0]?._index + 1);
        setClickedMonthIndex(ele[0]?._index);
      },
    };
    return optionsData;
  };

  const getTotal = (collection, key) => sumBy(collection, key);

  useEffect(() => {
    if (chargersData?.list?.length > 0) {
      if (!chargersData) {
        const cData = {};
        cData.list = [];
        cData.totalChargerRecordsCount = 0;
        setEnergyChartData(cData);
      }
      const finalEnergyData = {
        labels: months,
        datasets: [
          {
            data: [],
            backgroundColor: '#9ac55d',
            tooltips: {
              mode: 'label',
              callbacks: {
                label: () => '',
              },
            },
            datalabels: {
              display: true,
              formatter: (value) => `${round(value, 2)} kWh`,
              color: 'black',
              anchor: 'end',
              offset: -20,
              align: 'start',
            },
          },
        ],
      };

      const finalAmountData = {
        labels: months,
        datasets: [
          {
            data: [],
            backgroundColor: '#1a7bbb',
            tooltips: {
              mode: 'label',
              callbacks: {
                label: () => '',
              },
            },
            datalabels: {
              display: true,
              formatter: (value) => value,
              color: 'black',
              anchor: 'end',
              offset: -20,
              align: 'start',
            },
          },
        ],
      };

      const monthWiseEnergyData = [];
      const monthWiseAmountData = [];
      months.forEach((month, i) => {
        let energySold = 0;
        let amount = 0;
        const monthData = filter(chargersData?.list, { MONTH: i + 1 });
        if (monthData?.length > 0) {
          energySold = monthData[0]?.KWH_CONSUMED;
          amount = monthData[0]?.CHARGING_SESSIONS
            ? monthData[0]?.CHARGING_SESSIONS
            : 0;
        }
        monthWiseEnergyData.push(energySold);
        monthWiseAmountData.push(amount);
      });

      finalEnergyData.datasets[0].data = monthWiseEnergyData;
      finalAmountData.datasets[0].data = monthWiseAmountData;
      setEnergyChartData(finalEnergyData);
      setAmountChartData(finalAmountData);

      setEnergySoldTotal(
        toNumber(getTotal(chargersData?.list, 'KWH_CONSUMED')),
      );
      setNetRevenueTotal(
        toNumber(getTotal(chargersData?.list, 'CHARGING_SESSIONS')),
      );
    }
  }, [chargersData]);

  useEffect(() => {
    const currentYear = new Date().getFullYear();
    setMaxYear(currentYear);
    setSelectedYear(currentYear);
    // notifyYearChange(currentYear);
    setClickedMonthIndex(new Date().getMonth());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <>
      <div style={{ backgroundColor: '#ffffff' }}>
        <Spin
          indicator={<LoadingOutlined />}
          spinning={isLoading || isFetching}
        >
          {chargersData?.list?.length > 0 && (
            <Row>
              <Col md={12} sm={24} className="text-left">
                <span
                  style={{
                    border: '1px solid #dedee1',
                    backgroundColor: '#ffffff',
                    borderRadius: '5px',
                    margin: '5px',
                    padding: '10px',
                  }}
                >
                  Energy Sold: {`${energySoldTotal}KWh`}
                </span>

                <span
                  style={{
                    border: '1px solid #dedee1',
                    backgroundColor: '#ffffff',
                    borderRadius: '5px',
                    margin: '5px',
                    padding: '10px',
                  }}
                >
                  Charging Sessions: {`${netRevenueTotal}`}
                </span>
              </Col>
            </Row>
          )}
          <Row>
            <Col
              className="text-left"
              style={{
                fontWeight: 600,
                margin: '10px 0',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                width: '100%',
              }}
            >
              <LeftCircleOutlined
                onClick={() => yearChangeHandler('prev')}
                style={{
                  marginRight: '10px',
                  color: selectedYear > minYear ? '#2675b8' : 'grey',
                }}
              />
              <span style={{ fontSize: '18px' }}>Year {selectedYear}</span>
              <RightCircleOutlined
                onClick={() => yearChangeHandler('next')}
                style={{
                  marginLeft: '10px',
                  color: selectedYear < maxYear ? '#2675b8' : 'grey',
                }}
              />
            </Col>
          </Row>
          {chargersData?.list?.length > 0 ? (
            <>
              <Row style={{ justifyContent1: 'center' }}>
                {energyChartData ? (
                  <>
                    <Col sm="12" md="6" lg="6">
                      <div style={{ height: '250px' }}>
                        {energyChartData && (
                          <Bar
                            options={getOptions('energy')}
                            data={energyChartData}
                          />
                        )}
                      </div>
                    </Col>
                    <Col sm="12" md="6" lg="6">
                      <div style={{ height: '250px' }}>
                        {amountChartData && (
                          <Bar
                            options={getOptions('amount')}
                            data={amountChartData}
                          />
                        )}
                      </div>
                    </Col>
                  </>
                ) : (
                  <Col md={24} sm={24} lg={24}>
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                  </Col>
                )}
              </Row>
            </>
          ) : (
            <Card>
              <CardBody>
                <div
                  style={{
                    height: 250,
                    width: '100%',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                >
                  We dont have any Analytics report
                </div>
              </CardBody>
            </Card>
          )}
        </Spin>
      </div>

      {(selectedYear || filterMonth) && (
        <div style={{ marginTop: 50 }}>
          <CorporateChargingSessionsGrid
            CPID={chargerId}
            filterYear={selectedYear}
            filterMonth={filterMonth}
          />
        </div>
      )}
    </>
  );
};

export default ChargerAnalytics;
